import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

// Dev-only quick identity switch for testing. Rendering this button is just
// a convenience — the real gate is server-side (devUserSwitchEnabled
// reflects the backend's own APP_ENV check, and /auth/dev-switch re-verifies
// both the environment and that the caller is one of these same four fixed
// identities on every call), so there's no path where hiding this button
// alone would matter.
//
// Visible to Super Admin AND to any of the three dev-seed identities
// themselves — not just Super Admin — so switching FROM Storekeeper 1 TO
// Storekeeper 2/Manager/Super Admin works. Mirrors isDevSwitchCaller on the
// backend (see controllers/auth.js); this list is display-only, the backend
// re-checks independently.
const DEV_USERNAMES = ['dev_storekeeper1', 'dev_storekeeper2', 'dev_manager'];

const TARGETS = [
  { key: 'super_admin', label: 'Super Admin' },
  { key: 'storekeeper1', label: 'Storekeeper 1' },
  { key: 'storekeeper2', label: 'Storekeeper 2' },
  { key: 'manager', label: 'Manager' },
];

export default function DevUserSwitcher() {
  const { user, devUserSwitchEnabled, devSwitchUser } = useAuth();
  const [switching, setSwitching] = useState(null);
  const [error, setError] = useState('');

  const isDevSwitchIdentity = user?.role === 'super_admin' || DEV_USERNAMES.includes(user?.username);
  if (!isDevSwitchIdentity || !devUserSwitchEnabled) return null;

  const handleSwitch = async (target) => {
    setSwitching(target);
    setError('');
    try {
      await devSwitchUser(target);
      // Full reload, not client-side navigation — every page's already-loaded
      // data/permission checks were computed for the previous identity.
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.message || 'Switch failed');
      setSwitching(null);
    }
  };

  return (
    <div className="px-3 pb-3">
      <div className="rounded-lg border border-dashed border-amber-400/50 bg-amber-500/10 p-2.5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300 mb-2">Dev User Switcher</p>
        <div className="grid grid-cols-2 gap-1.5">
          {TARGETS.map((t) => (
            <button
              key={t.key}
              onClick={() => handleSwitch(t.key)}
              disabled={!!switching}
              className="px-2 py-1.5 rounded-md text-[11px] font-medium bg-white/10 text-amber-100 hover:bg-white/20 disabled:opacity-50 transition-colors truncate"
              title={t.label}
            >
              {switching === t.key ? '…' : t.label}
            </button>
          ))}
        </div>
        {error && <p className="text-[11px] text-red-300 mt-1.5">{error}</p>}
      </div>
    </div>
  );
}
